import { Box, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';

type Stage = 'fetching' | 'building' | 'done' | 'printed';

interface ExportProgressToastProps {
  title: string;
  /** "Excel" or "PDF". */
  format: string;
  loaded: number;
  /** 0 until the first page reports the real count. */
  total: number;
  stage: Stage;
}

const ExportProgressToast = ({ title, format, loaded, total, stage }: ExportProgressToastProps) => {
  const done = stage === 'done' || stage === 'printed';
  const pct = done || stage === 'building' ? 100 : total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  const known = done || total > 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <CircularProgress
          variant={known && stage !== 'building' ? 'determinate' : 'indeterminate'}
          value={pct}
          size={42}
          thickness={4.5}
          sx={{ color: done ? '#2e7d32' : '#488dff' }}
        />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>
            {known && stage !== 'building' ? `${pct}%` : ''}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
          {stage === 'printed' ? `${title} ready to print` : done ? `${title} downloaded` : `Downloading ${title}`}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#6b7280' }}>
          {stage === 'printed' ? 'Print dialog opened' : done ? `${format} saved` : stage === 'building' ? `Preparing ${format}…` : `${format} · please wait…`}
        </Typography>
      </Box>
    </Box>
  );
};

/** Download progress, bottom-right like a browser download tray; lives at app root so it survives navigation. */
export const createExportProgress = (title: string, format: string) => {
  const render = (stage: Stage, loaded = 0, total = 0) =>
    <ExportProgressToast title={title} format={format} loaded={loaded} total={total} stage={stage} />;
  const id = toast(render('fetching'), {
    position: 'bottom-right',
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    hideProgressBar: true,
  });

  return {
    id,
    update: (loaded: number, total: number) => toast.update(id, { render: render('fetching', loaded, total) }),
    building: () => toast.update(id, { render: render('building') }),
    finish: (loaded: number) => toast.update(id, { render: render('done', loaded, loaded), autoClose: 4000 }),
    printed: () => toast.update(id, { render: render('printed'), autoClose: 4000 }),
    fail: (message: string) => toast.update(id, { render: message, type: 'error', autoClose: 4000, hideProgressBar: false }),
  };
};

/**
 * Toast for a download that can't report progress. `work` resolving to false means nothing was
 * downloaded (e.g. a validation stop), 'print' means it opened the print dialog instead.
 */
export const trackExport = (title: string, format: string, work: Promise<unknown>) => {
  const progress = createExportProgress(title, format);
  work.then(
    (result) => {
      if (result === false) toast.dismiss(progress.id);
      else if (result === 'print') progress.printed();
      else progress.finish(0);
    },
    (error: any) => progress.fail(error?.message || `Could not download ${title}. Please try again.`),
  );
};

export default ExportProgressToast;
